import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, getToken, setToken } from '../utils/api';

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const EMPTY_COUNTS = { interests: 0, chats: 0, notifications: 0 };

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(!!getToken());
  const [meta, setMeta] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const toastId = useRef(0);

  const toast = useCallback((text, tone = 'default') => {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setCounts(EMPTY_COUNTS);
  }, []);

  const startSession = useCallback(({ token, user: u }) => {
    setToken(token);
    setUser(u);
  }, []);

  const refreshUser = useCallback(async () => {
    const { user: u } = await api('/user/me');
    setUser(u);
    return u;
  }, []);

  const refreshCounts = useCallback(async () => {
    if (!getToken()) return;
    try {
      const [i, c, n] = await Promise.all([api('/interests/summary'), api('/chat'), api('/notifications')]);
      setCounts({ interests: i.received, chats: c.unread, notifications: n.unread });
    } catch {
      /* counts are decorative; ignore transient failures */
    }
  }, []);

  useEffect(() => {
    api('/public/meta').then(setMeta).catch(() => {});
    if (getToken()) refreshUser().catch(logout).finally(() => setBooting(false));
    const onExpired = () => {
      logout();
      window.location.assign('/login');
    };
    window.addEventListener('cts:logout', onExpired);
    return () => window.removeEventListener('cts:logout', onExpired);
  }, [refreshUser, logout]);

  useEffect(() => {
    if (!user) return undefined;
    refreshCounts();
    const t = setInterval(refreshCounts, 15000);
    return () => clearInterval(t);
  }, [user?._id, refreshCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPremium = !!(user?.premium?.expiresAt && new Date(user.premium.expiresAt) > new Date());

  const value = useMemo(
    () => ({ user, setUser, booting, meta, isPremium, startSession, logout, refreshUser, toast, toasts, counts, refreshCounts }),
    [user, booting, meta, isPremium, startSession, logout, refreshUser, toast, toasts, counts, refreshCounts],
  );
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
